import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { isEmpty } from 'lodash';
import { toast } from 'react-toastify';
import { Container, Button, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useNavigate, Link } from 'react-router-dom';
import { ReactSVG } from 'react-svg';
import moment from 'moment/moment';
import Loader from '../../../components/Loader';
import TableHeadComponent from '../../../components/PracticeSetComponents/TableHeadComponent';
import TableContentsComponent from '../../../components/PracticeSetComponents/TableContentsComponent';
import {
	paymentSessionCreate,
	subscriptionList,
	currentPlan,
	stopAutoRecurring
} from '../../../redux/actions';
import ErrorHandleComponent from '../../../components/ErrorHandleComponent';
import {
	discounts,
	tableContents,
	tableHeadContents
} from '../../../constants/PricingPageData/index';
import InfoCircleIcon from '../../../assets/images/icons/info-circle-icon.svg';
import './styles.scss';
import CustomModal from '../../../components/CustomModal';

const PricingPage = () => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const shouldRender = useRef(true);
	const [autoRenewalModal, setAutoRenewalModal] = useState(false);
	const {
		loading,
		paymentSessionUrl,
		currentplaneStatus,
		paymentSessionError,
		stopAutoRecurringSuccess,
		stopAutoRecurringError
	} = useSelector((state) => state.payment);
	const { isLoggedIn, loading: authLoading } = useSelector((state) => state.auth);

	useEffect(() => {
		if (shouldRender.current && isLoggedIn) {
			shouldRender.current = false;
			dispatch(currentPlan());
		}
		if (currentplaneStatus?.status_code === 400 && isLoggedIn) {
			dispatch(subscriptionList());
			dispatch(paymentSessionCreate());
		}
	}, [currentplaneStatus, isLoggedIn]);

	useEffect(() => {
		if (stopAutoRecurringSuccess && stopAutoRecurringSuccess.status_code === 200) {
			toast.success(stopAutoRecurringSuccess.message);
			dispatch(currentPlan());
		} else if (stopAutoRecurringError) {
			toast.error(stopAutoRecurringError);
		}
	}, [stopAutoRecurringSuccess, stopAutoRecurringError]);

	if (loading || authLoading) {
		return (
			<div className='loader_section'>
				<Loader />
			</div>
		);
	}

	if (currentplaneStatus.message === 'Network Error') {
		return (
			<ErrorHandleComponent errorMessage={currentplaneStatus.message} />
		);
	}

	if (!isEmpty(paymentSessionError)) {
		return (
			<ErrorHandleComponent errorMessage={paymentSessionError} />
		);
	}

	const handleStopAutoRecurringModal = () => {
		setAutoRenewalModal(!autoRenewalModal);
	};

	const handleStopAutoRecurring = () => {
		dispatch(stopAutoRecurring());
		setAutoRenewalModal(false);
	};

	const autoRenewaFooter = [
		{
			label: 'Cancel',
			variant: 'danger',
			onClick: () => setAutoRenewalModal(false)
		},
		{
			label: 'OK',
			variant: 'primary',
			onClick: handleStopAutoRecurring
		}
	];

	return (
		<div className='pricing_page'>
			<section className='inner_banner_section'>
				<Container>
					<h1>Pricing</h1>
				</Container>
			</section>
			<div className='pricing_content'>
				<Container>
					<p style={{ color: 'red' }}>
						<b>Notice:</b> {' '}
						Your payment is secure with <a
							href='https://stripe.com/'
							target='_blank'
							rel='noreferrer'
						>
							Stripe
						</a>, but there seems to be some issue with our payment system.
						If you get double charged,
						OR if it auto-renews after an year even though you have turned it off,
						PLEASE let me know and I will take care of it.
						THANK YOU <span style={{ fontSize: '18px' }}>&#x1F64F;</span> for your
						understanding, while we work on making our payment sysyem more robust.
					</p>
					<p style={{ color: 'red' }}>
						One more thing, our '<em>Premium</em>' subscription is mostly symbolic at present.
						You can get the same facilities with the <Link to='/forms/special1'>Special1</Link> access,
						for FREE.
					</p>
					<hr></hr>
					<p>
						Thank you for your support! We won’t let you down. {' '}
						<span style={{ fontSize: '18px' }}>&#x1F64F;</span>
					</p>
					<p>
						We offer a subscription model where pricing is based on
						access to different products and services.
						We will revise our pricing model as we evolve.
						Our goal is to provide the best learning experience at a sustainable
						price. <mark>Discounts are available if you sign up early; scroll
						down for details</mark>.
					</p>
					<div className='pricing_table'>
						<div className='inner_table'>
							<table>
								<thead>
									<tr>
										{tableHeadContents.map((e, i) => {
											return (
												<TableHeadComponent key={i} data={e} />
											);
										})}
									</tr>
								</thead>
								<tbody>
									{tableContents.map((e, i) => {
										return (
											<TableContentsComponent key={i} data={e} />
										);
									})}
									{
										!isLoggedIn && (
											<tr>
												<td></td>
												<td></td>
												<td>
													<Button onClick={() => navigate('/signup')}>
														Select this
														<OverlayTrigger
															placement='bottom'
															overlay={
																<Tooltip>
																	It will take you to sign-up
																</Tooltip>
															}
														>
															<ReactSVG src={InfoCircleIcon} />
														</OverlayTrigger>
													</Button>
												</td>
												<td>
													<Button onClick={() => navigate('/signup')}>
														Select this
														<OverlayTrigger
															placement='bottom'
															overlay={
																<Tooltip>
																	It will take you to sign-up.
																	After signing-up you can upgrade
																	to premium.
																</Tooltip>
															}
														>
															<ReactSVG src={InfoCircleIcon} />
														</OverlayTrigger>
													</Button>
												</td>
											</tr>
										)
									}
									{
										isLoggedIn && currentplaneStatus.error && (
											<tr>
												<td></td>
												<td></td>
												<td></td>
												<td>

													<Button href={paymentSessionUrl} target={'_blank'}>
														Upgrade to Premium
													</Button>
												</td>
											</tr>
										)
									}
								</tbody>
							</table>
							<p>
								*1 <strong>On-demand Practice</strong> is available for limited question sets.
								We will be expanding it to other question sets over time.
							</p>
							<p>
								*2 <strong>Adaptive Practice</strong> is available for limited question sets.
								We will be expanding it to other question sets over time.
							</p>
							<p>
								*3 Basic accounts have access to a simple dashboard for tracking weekly progress.
								Premium accounts have additional access to practice history for the last four weeks.
								An enhanced dashboard is in development for Premium users,
								which will offer personalization and actionable analytics.
							</p>
						</div>
						{
							isLoggedIn && !currentplaneStatus.error && (
								<div
									className={`pricing_page_expires 
									${currentplaneStatus?.data?.auto_recurring ? '' : 'text-right'}`}>
									<p>Your current subscription expires on:{' '}
										{moment(currentplaneStatus?.data?.plan_end_date,
											'ddd, DD MMM YYYY HH:mm:ss z').format('DD MMM YYYY')}</p>
									{
										currentplaneStatus?.data?.auto_recurring
											? <div>
												<p>Your subscription will renew automatically 30 days before expiry.</p>
												<p> You can
													<Button onClick={() => handleStopAutoRecurringModal()}>
														opt out of auto renewal
													</Button> anytime.
												</p>
											</div>
											:	 <div>
												<p>You can renew for another year on or after:&nbsp;
													<span>
														{moment(currentplaneStatus?.data?.plan_end_date,
															'ddd, DD MMM YYYY HH:mm:ss z')
															.subtract(30, 'days').format('DD MMM YYYY')}
													</span>
												</p>
												<p>While renewing you can opt for <strong>auto renewal</strong>.</p>
											</div>
									}
								</div>
							)
						}
						<div className='pricing-info' id="discount-section">
							<h3>^ Your payment is secure ...</h3>
							<p>
								We (boo9 inc.) do not store your credit card information.
								Your payment is processed securely through{' '}
								<a
									href='https://stripe.com/'
									target='_blank'
									rel='noreferrer'
								>
									Stripe
								</a>.
							</p>
							<h3>* Lifetime discount for the first 1M <i>Premium</i> users:</h3>
							<p>
								<b>Lifetime discount:</b>{' '}
								We are committed to bringing you world-class content over time, which requires
								a lot of investment. So to encourage early sign-ups we are offering <mark>lifetime
								discount</mark> for the first 1M <i>premium</i> users. <u>Discount will be applied
								automatically during checkout</u> based on your <i>premium</i> user number. Larger
								discount for earlier <i>premium</i> users, calculated based on the following table.
							</p>
							<div className='discount_table'>
								<table>
									<thead>
										<tr>
											<th>Premium user no.</th>
											<th>Discount code</th>
											<th>Discount</th>
										</tr>
									</thead>
									<tbody>
										{discounts.map((e, i) => {
											return (
												<tr key={i}>
													<td>{e.user_no}</td>
													<td>{e.discount_code}</td>
													<td>{e.discount}</td>
												</tr>
											);
										})}
									</tbody>
								</table>
							</div>
							<div className='text'>
								Terms:
								<ol>
									<li>
										<u>Lifetime meaning</u> <strong>if there is no break in the subscription, your %
										discount
										will be valid for a lifetime.</strong> You will continue to receive the same
										% discount even if we increase the subscription price later. This discount
										will be void if there is a break in the subscription.
									</li>
									<li>
										<strong>We reserve the right to end this offer anytime without notice</strong>,
										even before we reach 1M premium users.
									</li>
								</ol>
							</div>
							<h3>! <mark>Cannot afford to pay?</mark></h3>
							<div className='text'>
								<p>
									No worries!
									We do not turn anyone away simply because they cannot pay.
									You can apply for <b>Special1</b> access.
								</p>
								<p>
									<b>Special1</b> users get <b>Premium</b> access for FREE
									for a few months (which can be renewed).
									Click <Link to='/forms/special1'>here</Link>{' '}
									to apply for <b>Special1</b> access.
								</p>
							</div>
							<p>
								If you can, please support our YouTube channel, so we can provide more for FREE:
								<ol>
									<li><b>Subscribe</b> to our{' '}
										<a
											href='https://www.youtube.com/@boo9X'
											target='_blank'
											rel='noreferrer'
										>
											YouTube
										</a> channel if you like
										(it gives us motivation{' '}
										+ you will be notified when we post new videos)</li>
									<li><b>Comment</b> on a{' '}
										<a
											href='https://www.youtube.com/@boo9X'
											target='_blank'
											rel='noreferrer'
										>
											video
										</a> if you find it helpful
										(it helps in YouTube ranking + gives us feedback)</li>
									<li><b>Share</b> our{' '}
										<a
											href='https://www.youtube.com/@boo9X'
											target='_blank'
											rel='noreferrer'
										>
											YouTube
										</a> channel with your friends/teachers
										(word of mouth is very powerful)</li>
								</ol>
							</p>
							<p>
								We truly appreciate your support,
								THANK YOU <span style={{ fontSize: '18px' }}>&#x1F64F;</span>.
								If you have any questions,
								feel free to <Link to='/contact'>reach out to us</Link>.
							</p>
						</div>
					</div>
				</Container>
			</div>
			<CustomModal
				title='Opt-Out of Auto-Renewal'
				content={<p>Are You Sure You Want to Opt-Out of Auto-Renewal?</p>}
				footerButtons={autoRenewaFooter}
				show={autoRenewalModal}
				handleClose={handleStopAutoRecurringModal}
			/>
		</div>
	);
};

export default PricingPage;
